/**
 * Landed-cost list — `/purchasing/landed-costs`. Cursor DataTable over
 * `GET /api/v1/landed-costs` with the contract's status / supplier / created
 * filters. Amounts render from wire decimal strings via MoneyDisplay.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Button,
  Combobox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";

import { DataTable, dateTimeCell, textCell } from "@/components/data-table";
import type { SortState } from "@/components/data-table";
import { MoneyDisplay } from "@/components/money/money-display";

import {
  LANDED_COST_STATUSES,
  type LandedCostDocumentSummary,
  type LandedCostStatus,
} from "../api";
import { useLandedCostList, type LandedCostFilters } from "../queries";
import { useSupplierNames } from "../components/lookup-names";
import { LandedCostStatusBadge } from "../components/status-badges";
import { enumLabel } from "../labels";

const ALL = "__all__";

export interface LandedCostListPageProps {
  onOpenLandedCost: (landedCostId: string) => void;
  onCreateLandedCost: () => void;
  canCreate?: boolean;
  canReadSuppliers?: boolean;
  pageLimit?: number;
}

export function LandedCostListPage({
  onOpenLandedCost,
  onCreateLandedCost,
  canCreate = false,
  canReadSuppliers = true,
  pageLimit,
}: LandedCostListPageProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<LandedCostStatus | typeof ALL>(ALL);
  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [createdFrom, setCreatedFrom] = useState("");
  const [sort, setSort] = useState<SortState>(null);

  const { supplierName, parties } = useSupplierNames(canReadSuppliers);
  const supplierOptions = useMemo(
    () => parties.map((party) => ({ value: party.id, label: party.display_name })),
    [parties],
  );

  const filters = useMemo<LandedCostFilters>(() => {
    const built: LandedCostFilters = {};
    if (status !== ALL) built.status = status;
    if (supplierId !== null && supplierId !== "") built.supplier_id = supplierId;
    if (createdFrom !== "") built.created_from = `${createdFrom}T00:00:00Z`;
    return built;
  }, [status, supplierId, createdFrom]);

  const hasFilters = Object.keys(filters).length > 0;
  const query = useLandedCostList(filters, sort, pageLimit);

  const columns = useMemo<ColumnDef<LandedCostDocumentSummary>[]>(
    () => [
      {
        accessorKey: "document_number",
        id: "document_number",
        header: t("purchasing.landedCostList.columns.number", "Document"),
        cell: (ctx) => (
          <span dir="ltr" className="font-mono font-medium">
            {ctx.getValue<string>()}
          </span>
        ),
      },
      {
        accessorKey: "supplier_id",
        id: "supplier",
        header: t("purchasing.landedCostList.columns.supplier", "Supplier"),
        enableSorting: false,
        cell: (ctx) => textCell(supplierName(ctx.getValue<string | undefined>())),
      },
      {
        accessorKey: "status",
        id: "status",
        header: t("purchasing.landedCostList.columns.status", "Status"),
        enableSorting: false,
        cell: (ctx) => <LandedCostStatusBadge status={ctx.getValue<LandedCostStatus>()} />,
      },
      {
        accessorKey: "total_amount",
        id: "total_amount",
        header: t("purchasing.landedCostList.columns.total", "Total"),
        enableSorting: false,
        meta: { numeric: true },
        cell: (ctx) => (
          <MoneyDisplay
            value={{ amount: ctx.getValue<string>(), currency: ctx.row.original.currency }}
          />
        ),
      },
      {
        accessorKey: "base_total_amount",
        id: "base_total_amount",
        header: t("purchasing.landedCostList.columns.baseTotal", "Base total"),
        enableSorting: false,
        meta: { numeric: true },
        cell: (ctx) => (
          <MoneyDisplay
            value={{ amount: ctx.getValue<string>(), currency: ctx.row.original.base_currency }}
          />
        ),
      },
      {
        accessorKey: "posted_at",
        id: "posted_at",
        header: t("purchasing.landedCostList.columns.postedAt", "Posted"),
        cell: (ctx) => {
          const value = ctx.getValue<string | undefined>();
          return value === undefined ? textCell(undefined) : dateTimeCell(value);
        },
      },
      {
        accessorKey: "created_at",
        id: "created_at",
        header: t("purchasing.landedCostList.columns.created", "Created"),
        cell: (ctx) => dateTimeCell(ctx.getValue<string>()),
      },
    ],
    [t, supplierName],
  );

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">
            {t("purchasing.landedCostList.title", "Landed costs")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t(
              "purchasing.landedCostList.subtitle",
              "Freight, duty and insurance allocated onto received stock.",
            )}
          </p>
        </div>
        {canCreate ? (
          <Button onClick={onCreateLandedCost}>
            {t("purchasing.landedCostList.newDocument", "New landed cost")}
          </Button>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="purchasing-lc-status">
            {t("purchasing.landedCostList.filters.status", "Status")}
          </Label>
          <Select
            value={status}
            onValueChange={(value) => setStatus(value as LandedCostStatus | typeof ALL)}
          >
            <SelectTrigger id="purchasing-lc-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("purchasing.common.all", "All")}</SelectItem>
              {LANDED_COST_STATUSES.map((value) => (
                <SelectItem key={value} value={value}>
                  {enumLabel(t, "landedCostStatus", value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {canReadSuppliers ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="purchasing-lc-supplier">
              {t("purchasing.landedCostList.filters.supplier", "Supplier")}
            </Label>
            <Combobox
              id="purchasing-lc-supplier"
              options={supplierOptions}
              value={supplierId}
              onChange={setSupplierId}
              placeholder={t("purchasing.common.anySupplier", "Any supplier")}
              searchPlaceholder={t("purchasing.common.searchSupplier", "Search supplier…")}
              emptyMessage={t("purchasing.common.noSuppliers", "No suppliers found.")}
            />
          </div>
        ) : null}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="purchasing-lc-created-from">
            {t("purchasing.landedCostList.filters.createdFrom", "Created from")}
          </Label>
          <Input
            id="purchasing-lc-created-from"
            type="date"
            dir="ltr"
            value={createdFrom}
            onChange={(event) => setCreatedFrom(event.target.value)}
          />
        </div>
      </div>

      {hasFilters ? (
        <div>
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs"
            onClick={() => {
              setStatus(ALL);
              setSupplierId(null);
              setCreatedFrom("");
            }}
          >
            {t("purchasing.common.clearFilters", "Clear filters")}
          </Button>
        </div>
      ) : null}

      <DataTable
        columns={columns}
        query={query}
        getRowId={(row) => row.id}
        sort={sort}
        onSortChange={setSort}
        onRowClick={(row) => onOpenLandedCost(row.id)}
        storageKey="purchasing.landed-costs.list.columns"
        emptyMessage={
          hasFilters
            ? t("purchasing.landedCostList.emptyFiltered", "No landed costs match these filters.")
            : t("purchasing.landedCostList.empty", "No landed costs yet.")
        }
        errorMessage={t("purchasing.landedCostList.loadError", "Landed costs could not be loaded.")}
      />
    </div>
  );
}
